import { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import { getColor, ListFilter, Tag, WithTooltip } from 'basic-components';
import { DataStorageEnums, SymbolSearchQuery } from 'dashboard-services/api/dataStorage';
import Query from 'dashboard-services/api/Query';
import { DropdownService } from 'primary-components';

import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/modules/datastorage';
import properties from 'resources/constants/properties.json';

import { useQueryClient } from '@tanstack/react-query';

import './SymbolFilter.scss';

const EMPTY_ARRAY = [];
const SymbolFilter = ({ groupName = undefined, query = "", setGroupName, tags, setTags }) => {
  const dispatch = useDispatch(),
        [key, setKey] = useState(),
        [keyQuery, setKeyQuery] = useState(),
        [valueQuery, setValueQuery] = useState(),
        [groupInputQuery, setGroupInputQuery] = useState(""),
        [searchParams] = useSearchParams(),
        provider = useMemo(() => searchParams.get("provider") || undefined, [searchParams]),
        queryClient = useQueryClient(),
        symbolParams = useMemo(() => tags.filter(tag => tag.type === DataStorageEnums.KEY_TYPE.SYMBOL), [tags]),
        metadataParams = useMemo(() => tags.filter(tag => tag.type === DataStorageEnums.KEY_TYPE.METADATA), [tags]),
        filterSymbolObj = useMemo(() => {
          const result = {};
          symbolParams.forEach(({ key, value }) => {
            if(value !== undefined && value !== null) {
              result[key] = value === '*' ? [] : [value];
            }
          });
          return result;
        }, [symbolParams]),
        filterMetadataObj = useMemo(() => {
          const result = {};
          metadataParams.forEach(({ key, value }) => {
            if(value !== undefined && value !== null) {
              result[key] = value === '*' ? [] : [value];
            }
          });
          return result;
        }, [metadataParams]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
        , [getApiConfigLocal]),
        getGroupQueryBuilder = useCallback(() =>
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withHeader("Target", provider, true)
        , [getApiConfigLocal, provider]),
        selectGroups = useCallback(data => data?.pages?.flatMap(page => page?.items)?.map(item => ({ value: item.groupName, label: item.groupName })), []),
        groupQueryCalculated = useMemo(() => new SymbolSearchQuery.Builder()
            .withGroupName(`*${String(groupInputQuery).toLowerCase()}*`, false)
            .withSymbols(symbolParams)
            .withMetadatas(metadataParams)
            .withQueryString(query ? `${query}${query.startsWith('"') && query.endsWith('"') ? "" : "*"}` : "")
            .build()
            .getQuery()
        , [groupInputQuery, metadataParams, query, symbolParams]),
        { data: groups = EMPTY_ARRAY, fetchNextPage: fetchMoreGroups, hasNextPage: hasNextPageGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups } = dataStorageQueries.useSearchSymbols({
          getQueryBuilder: getGroupQueryBuilder,
          query: groupQueryCalculated,
          collapse: "groupName",
          sortBy: "groupName.keyword=asc",
          shouldRefetch: false,
          size: 20,
          select: selectGroups,
          provider
        }),
        selectKeys = useCallback(data => data?.pages?.flatMap(page => page?.items)?.map(item => ({
          value: item.name,
          label: item.name,
          color: item.type === DataStorageEnums.KEY_TYPE.METADATA ? getColor("grey-dark") : undefined,
          type: item.type
        })), []),
        { data: keys = EMPTY_ARRAY, fetchNextPage: fetchMoreKeys, hasNextPage: hasNextPageKeys, isFetching: isFetchingKeys, isFetchingNextPage: isFetchingNextPageKeys } = dataStorageQueries.useSearchSymbolKeys({
          getQueryBuilder,
          groupName,
          metadata: true,
          metadatas: filterMetadataObj,
          query: keyQuery ? `*${keyQuery}*` : '*',
          select: selectKeys,
          symbols: filterSymbolObj,
          useEs: properties.useEsSymbolListing === "true"
        }),
        keyOptions = useMemo(() => keys.map(({ color, label, ...option }) => ({
          ...option,
          label: color ? <span style={{ color }}>{label}</span> : label
        })), [keys]),
        { data: values = EMPTY_ARRAY, fetchNextPage: fetchMoreValues, hasNextPage: hasNextPageValues, isFetching: isFetchingValues, isFetchingNextPage: isFetchingNextPageValues } = DropdownService.useQuerySymbolValues({
          getApiConfig: getApiConfigLocal,
          groupName,
          input: valueQuery,
          key: key?.value,
          metadata: true,
          metadatas: filterMetadataObj,
          queryClient,
          symbols: filterSymbolObj,
          useEs: properties.useEsSymbolListing === "true"
        }),
        metadataLabelProps = useMemo(() => ({
          style: {
            color: getColor("grey-dark")
          }
        }), []),
        changeKey = useCallback((_, { data }) => setKey(data || undefined), []),
        changeValue = useCallback((value, { data }) => {
          if(data === null) {
            return;
          }
          const index = tags.findIndex(tag => tag.key === key?.value)
          if(index > -1) {
            setTags(tags => tags.replace({ key: key?.value, value, type: data?.type }, index))
          } else {
            setTags(tags => tags.add({ key: key?.value, value, type: data?.type }))
          }
          setKey(undefined)
        }, [key, setTags, tags]),
        clearTag = useCallback((tagKey, value) => setTags(tags => tags.removeByProps({ key: tagKey, value })), [setTags])

  return (
    <Fragment>
      <ListFilter
          defaultValue={groupName}
          fetchNextPage={fetchMoreGroups}
          hasNextPage={hasNextPageGroups}
          isFetching={isFetchingGroups || isFetchingNextPageGroups}
          label="Group"
          name="groupName"
          onChange={setGroupName}
          onInputChange={setGroupInputQuery}
          options={groups}
      />
      <ListFilter
          defaultValue={key?.value}
          fetchNextPage={fetchMoreKeys}
          hasNextPage={hasNextPageKeys}
          isFetching={isFetchingKeys || isFetchingNextPageKeys}
          label="Symbol"
          name="symbolKey"
          onChange={changeKey}
          onInputChange={setKeyQuery}
          options={keyOptions}
      />
      {key && (
        <ListFilter
            fetchNextPage={fetchMoreValues}
            hasNextPage={hasNextPageValues}
            isFetching={isFetchingValues || isFetchingNextPageValues}
            label="Value"
            name="symbolValue"
            onChange={changeValue}
            onInputChange={setValueQuery}
            options={values}
        />
      )}
      {tags.length > 0 && (
        <div className="ng-symbol-picker__modal__symbol-filter__tags">
          {tags.map(({ key: tagKey, value, type }) => (
            <WithTooltip
                data-place="top"
                key={tagKey + value + type + "tag"}
                short
                text={type === DataStorageEnums.KEY_TYPE.METADATA ? "Metadata of symbol" : "Key of symbol"}
            >
              <Tag
                  labelProps={type === DataStorageEnums.KEY_TYPE.METADATA ? metadataLabelProps : undefined}
                  onClear={() => clearTag(tagKey, value)}
              >
                {`${tagKey}: ${value}`}
              </Tag>
            </WithTooltip>
          ))}
        </div>
      )}
    </Fragment>
  )
}

SymbolFilter.propTypes = {
  groupName: PropTypes.string,
  query: PropTypes.string,
  setGroupName: PropTypes.func.isRequired,
  setTags: PropTypes.func.isRequired,
  tags: PropTypes.array.isRequired,
}

export default memo(SymbolFilter);
